/**
 * TemplateFilters
 *
 * Responsive tag filter and sort controls for templates with basic-plan gating
 * and dropdown portals. Supports custom tag lists (mail vs standard).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, ChevronDown, Clock, TrendingUp, Shuffle, X, Lock } from 'lucide-react';
import { TAG_LIST, MAIL_TEMPLATE_TAG_LIST, VISIBLE_TAGS_COUNT } from '../../../data/tags/tagEnums';

// Remove the alphabetical sorting - use TAG_LIST directly to maintain the correct order
// const initialSortedTags = [...TAG_LIST].sort((a, b) => a.localeCompare(b));

const MAX_ACTIVE_FILTERS = 4; // Define the limit

const TemplateFilters = ({
  activeFilter: activeFilterProp,
  setActiveFilter: setActiveFilterProp,
  onSortChange,
  savedFilterActive = false,
  onToggleSavedFilter,
  isLoggedIn = false,
  isBasicUser = false, // Add new prop with default value
  tagList = TAG_LIST // Allow custom tag list, default to regular tags
}) => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [sortMethod, setSortMethod] = useState('random');
  const [showTooltip, setShowTooltip] = useState(null); // For tooltip display: 'sort', 'saved'
  const [showSortTooltip, setShowSortTooltip] = useState(false); // For sorting descriptions tooltip
  const [showCategoriesButton, setShowCategoriesButton] = useState(false); // For intermediate screen sizes

  // --- State ---
  const [maxVisibleTags, setMaxVisibleTags] = useState(() => {
    // Calculate initial based on screen width if possible, otherwise default
    if (typeof window !== 'undefined' && window.innerWidth < 640) return 0;
    // A more refined initial calculation could be done here, but VISIBLE_TAGS_COUNT is a fallback
    return VISIBLE_TAGS_COUNT;
  });
  const [activeFilters, setActiveFilters] = useState(() => {
    const initialActive = Array.isArray(activeFilterProp) ? activeFilterProp : (activeFilterProp ? [activeFilterProp] : []);
    return initialActive.filter(tag => tagList.includes(tag));
  });
  // visibleTags and hiddenTags will now be derived primarily by balanceTags effect
  const [visibleTags, setVisibleTags] = useState([]);
  const [hiddenTags, setHiddenTags] = useState([]);

  // Refs (unchanged)
  const categoriesMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  const sortMenuRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const filtersContainerRef = useRef(null);
  const tagsContainerRef = useRef(null);
  const controlsRef = useRef(null);

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [sortDropdownPosition, setSortDropdownPosition] = useState({ top: 0, left: 0 });

  // --- Effects ---

  // Propagate activeFilters changes upwards (unchanged)
  useEffect(() => {
    setActiveFilterProp(activeFilters.length > 0 ? activeFilters : null);
  }, [activeFilters, setActiveFilterProp]);

  // Update dropdown positions on scroll and window resize
  useEffect(() => {
    const updateDropdownPositions = () => {
      if (isMoreMenuOpen) {
        // Determine which button is visible based on screen size
        const buttonRef = window.innerWidth < 640 
          ? categoriesMenuRef.current 
          : moreMenuRef.current;
          
        if (buttonRef) {
          const rect = buttonRef.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: Math.min(rect.left + window.scrollX, window.innerWidth - 224)
          });
        }
      }
      if (isSortMenuOpen && sortMenuRef.current) {
        const rect = sortMenuRef.current.getBoundingClientRect();
        setSortDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: Math.min(rect.left + window.scrollX, window.innerWidth - 160)
        });
      }
    };
    
    if (isMoreMenuOpen || isSortMenuOpen) {
      window.addEventListener('scroll', updateDropdownPositions);
      window.addEventListener('resize', updateDropdownPositions);
      // Initial positioning
      updateDropdownPositions();
    }
    
    return () => {
      window.removeEventListener('scroll', updateDropdownPositions);
      window.removeEventListener('resize', updateDropdownPositions);
    };
  }, [isMoreMenuOpen, isSortMenuOpen]);


  // Central function to calculate and set visible/hidden tags
  const balanceTags = useCallback((currentMaxVisible, currentActiveFilters) => {
      // Get the original tagList to maintain proper order
      const allTags = [...tagList];
      
      // First, prioritize active filters
      // Pull active filters to the front while maintaining their tagList order
      const activeTagsInOrder = allTags.filter(tag => currentActiveFilters.includes(tag));
      const inactiveTagsInOrder = allTags.filter(tag => !currentActiveFilters.includes(tag));
      
      // Combine with active tags first, then inactive tags (both preserving tagList order)
      const orderedTags = [...activeTagsInOrder, ...inactiveTagsInOrder];
      
      // Split into visible and hidden tags
      const newVisible = orderedTags.slice(0, Math.max(1, currentMaxVisible));
      const newHidden = orderedTags.slice(Math.max(1, currentMaxVisible));
      
      // Handle zero visible tags case (mobile view)
      if (currentMaxVisible <= 0) {
          setVisibleTags([]);
          setHiddenTags(orderedTags);
      } else {
          // Set visible and hidden tags
          setVisibleTags(newVisible);
          setHiddenTags(newHidden);
      }

  }, [tagList]);

  // Handle responsive tag display calculation
  const handleResize = useCallback(() => {
      if (typeof window === 'undefined' || !filtersContainerRef.current || !tagsContainerRef.current || !controlsRef.current) return;

      const containerWidth = filtersContainerRef.current.clientWidth;
      const isMobileView = containerWidth < 640;
      const isIntermediateView = containerWidth >= 640 && containerWidth < 800; // Add intermediate view detection
      let calculatedMaxVisible;

      // Set the categories button visibility flag for intermediate views
      setShowCategoriesButton(isIntermediateView || isMobileView);

      if (isMobileView) {
          calculatedMaxVisible = 0;
      } else if (isIntermediateView) {
          // For intermediate views, show at least 1 tag if possible
          calculatedMaxVisible = 1;
      } else {
          // Desktop View Calculation
          const controlsWidth = controlsRef.current.clientWidth + 20; // Approx width + margin
          const availableWidth = containerWidth - controlsWidth;
          const averageTagWidth = 120; // Estimate

          // Estimate max visible based on available width
          let preliminaryMax = Math.max(1, Math.floor(availableWidth / averageTagWidth));
          preliminaryMax = Math.min(preliminaryMax, tagList.length);

          // Now, estimate if More button will be needed based on preliminaryMax
          const needsMoreButton = tagList.length > preliminaryMax;
          const moreButtonWidth = needsMoreButton ? 100 : 0; // Width of "More" button if needed

          // Recalculate with More button width accounted for
          calculatedMaxVisible = Math.max(1, Math.floor((availableWidth - moreButtonWidth) / averageTagWidth));
          calculatedMaxVisible = Math.min(calculatedMaxVisible, tagList.length); // Cannot show more than total tags
      }

      // Update maxVisibleTags state *if changed*
      setMaxVisibleTags(prevMax => {
          if (calculatedMaxVisible !== prevMax) {
              return calculatedMaxVisible;
          }
          return prevMax;
      });

  }, []); // Dependencies removed, refs accessed directly

  // Effect for initial resize and listener attachment
  useEffect(() => {
    handleResize(); // Run on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Effect to run balanceTags whenever activeFilters or maxVisibleTags changes
  useEffect(() => {
      balanceTags(maxVisibleTags, activeFilters);
  }, [activeFilters, maxVisibleTags, balanceTags]);


  // Notify parent component when sort method changes (unchanged)
  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortMethod);
    }
  }, [sortMethod, onSortChange]);

  // Close dropdowns on outside click (unchanged)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        ((categoriesMenuRef.current && !categoriesMenuRef.current.contains(event.target)) &&
        (moreMenuRef.current && !moreMenuRef.current.contains(event.target))) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsMoreMenuOpen(false);
      }
      if (
        sortMenuRef.current &&
        !sortMenuRef.current.contains(event.target) &&
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close sort tooltip when dropdown state changes
  useEffect(() => {
    if (isSortMenuOpen) {
      setShowSortTooltip(false);
    }
  }, [isSortMenuOpen]);


  // --- Handlers ---

  const activateTag = (tag) => {
    // Prevent activating if user is on basic plan
    if (isBasicUser) return;
    
    // Prevent activating more than the limit
    if (activeFilters.length >= MAX_ACTIVE_FILTERS) {
      // Optional: Add a toast notification here if a toast system is available/passed down
      console.warn(`Cannot select more than ${MAX_ACTIVE_FILTERS} filters.`);
      return; 
    }
    // Set activeFilters with the new tag at the front
    setActiveFilters(prevActive => [tag, ...prevActive.filter(t => t !== tag)]);
  };

  const deactivateTag = (tag) => {
    // Prevent deactivating if user is on basic plan
    if (isBasicUser) return;
    
    // Set activeFilters excluding the tag
    setActiveFilters(prevActive => prevActive.filter(t => t !== tag));
  };

  const clearAllFilters = () => {
    // Prevent clearing if user is on basic plan
    if (isBasicUser) return;
      
    setActiveFilters([]);
  };

  // Click on a VISIBLE tag button
  const handleToggleTag = (tag) => {
    // For basic users, do nothing
    if (isBasicUser) return;
    
    const isActive = activeFilters.includes(tag);
    if (isActive) {
      deactivateTag(tag);
    } else {
      // Check limit before activating
      if (activeFilters.length < MAX_ACTIVE_FILTERS) {
        activateTag(tag);
      }
    }
  };

  // Click on a tag in the MORE dropdown
  const handleMoreTagSelect = (tag) => {
    // For basic users, do nothing
    if (isBasicUser) return;
    
    const isActive = activeFilters.includes(tag);
    if (isActive) {
      // If ACTIVE in dropdown, just DEACTIVATE
      deactivateTag(tag);
    } else {
      // If INACTIVE in dropdown, ACTIVATE only if limit not reached
      if (activeFilters.length < MAX_ACTIVE_FILTERS) {
        activateTag(tag);
      }
    }
    // Don't close immediately if limit reached, user might need to deselect first
    if (activeFilters.length < MAX_ACTIVE_FILTERS || isActive) {
        setIsMoreMenuOpen(false); // Close dropdown if activation/deactivation occurred
    }
  };

  // Other Handlers (handleMoreClick, handleSortClick, handleSortSelect, handleSavedClick) - modified for basic users

  const handleMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // For basic users, do nothing
    if (isBasicUser) return;
    
    // Toggle the dropdown
    setIsMoreMenuOpen(prevState => !prevState);
  };

  const handleSortClick = (e) => {
    e.stopPropagation();
    
    // Close sort tooltip when dropdown is clicked
    setShowSortTooltip(false);
    
    // For basic users, just show tooltip and do nothing else
    if (isBasicUser) {
      setShowTooltip('sort');
      return;
    }
    
    if (sortMenuRef.current) {
      const rect = sortMenuRef.current.getBoundingClientRect();
      setSortDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: Math.min(rect.left + window.scrollX, window.innerWidth - 160)
      });
    }
    setIsSortMenuOpen(!isSortMenuOpen);
  };

  const handleSortSelect = (method) => {
    // For basic users, do nothing
    if (isBasicUser) return;
    
    setSortMethod(method);
    setIsSortMenuOpen(false);
    // onSortChange is called via useEffect
  };

  const handleSavedClick = () => {
    if (!isLoggedIn) {
      alert('Please log in to view your saved templates');
      return;
    }
    
    // For basic users, just show tooltip and do nothing else
    if (isBasicUser) {
      setShowTooltip('saved');
      return;
    }
    
    if (onToggleSavedFilter) {
      onToggleSavedFilter();
    }
  };

  // Tooltip handlers
  const handleMouseEnter = (type) => {
    if (isBasicUser) {
      setShowTooltip(type);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(null);
  };

  // Sort tooltip handlers for non-basic users
  const handleSortMouseEnter = () => {
    if (isBasicUser) {
      setShowTooltip('sort');
    } else if (!isSortMenuOpen) {
      setShowSortTooltip(true);
    }
  };

  const handleSortMouseLeave = () => {
    setShowTooltip(null);
    setShowSortTooltip(false);
  };

  // --- Render Helpers (getSortIcon, getSortText) - unchanged ---
  const getSortIcon = () => {
    if (sortMethod === 'newest') return <Clock className="h-4 w-4" />;
    if (sortMethod === 'popular') return <TrendingUp className="h-4 w-4" />;
    return <Shuffle className="h-4 w-4" />;
  };

  const getSortText = () => {
    if (sortMethod === 'newest') return 'Newly Added';
    if (sortMethod === 'popular') return 'Most Popular';
    return 'Randomized';
  };

  // Get sort description for tooltip
  const getSortDescription = () => {
    if (sortMethod === 'newest') return 'Shows the latest templates we\'ve added to the platform';
    if (sortMethod === 'popular') return 'Sorted by how frequently our users have used these templates';
    return 'Templates are shuffled once daily, so you can maintain your place while browsing';
  };

  // --- JSX (Structure mostly unchanged, logic relies on state) ---
  const isFilterLimitReached = activeFilters.length >= MAX_ACTIVE_FILTERS;

  // Check if this is mail templates to hide tags bar
  const isMailTemplates = tagList === MAIL_TEMPLATE_TAG_LIST;

  return (
    <div className="mb-4" ref={filtersContainerRef}>
      <div className="flex items-center justify-between mb-2">
        {/* Left side - Tags / Categories - Hidden for mail templates */}
        {!isMailTemplates && (
          <div
            ref={tagsContainerRef}
            className="flex-1 overflow-x-auto pb-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 -mx-1 px-1"
          >
            <div className="flex space-x-2 min-w-min">
              {/* Categories Button - visible only when no visible tags AND on mobile/intermediate viewport sizes */}
              {visibleTags.length === 0 && (
                <div 
                  ref={categoriesMenuRef} 
                  className={showCategoriesButton ? "block sm:block relative" : "block sm:hidden relative"}
                >
                  <button
                    onClick={handleMoreClick}
                    className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0 flex items-center gap-1
                    ${isBasicUser ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 h-10`}
                  >
                    Categories <ChevronDown className={`h-4 w-4 transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              )}

              {/* Desktop Tag Filters - Renders when there are visible tags OR when desktop with no categories button */}
              <div className={visibleTags.length > 0 || maxVisibleTags > 0 ? "hidden sm:flex space-x-2" : "hidden space-x-2"}>
                {visibleTags.map((tag) => {
                  const isActive = activeFilters.includes(tag);
                  const isDisabled = isBasicUser || (!isActive && isFilterLimitReached);
                  return (
                    <button
                      key={tag}
                      onClick={() => handleToggleTag(tag)}
                      disabled={isDisabled}
                      className={`
                        px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0
                        transition-colors duration-150 ease-in-out my-1
                        ${isActive
                          ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 ring-1 ring-purple-300 dark:ring-purple-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}
                        ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}
                      `}
                    >
                      {tag}
                    </button>
                  )
                })}

                {/* More dropdown button - Show whenever there are hidden tags */}
                {hiddenTags.length > 0 && (
                  <div 
                    ref={moreMenuRef} 
                    className="relative my-1 inline-block"
                  >
                    <button
                      onClick={handleMoreClick}
                      className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0 flex items-center gap-1
                        ${isBasicUser ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700`}
                    >
                      More <ChevronDown className={`h-4 w-4 transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Right side controls (unchanged structure) */}
        <div ref={controlsRef} className="flex items-center gap-2 pl-2 my-1">
          {/* Saved filter */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('saved')}
            onMouseLeave={handleMouseLeave}
          >
            <button
              onClick={handleSavedClick}
              disabled={isBasicUser}
              className={`
                flex items-center justify-center gap-1 px-3 sm:px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium border
                ${savedFilterActive
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-500 dark:border-purple-600'
                  : isBasicUser
                    ? 'bg-white text-gray-500 border-gray-200 opacity-60 cursor-not-allowed dark:bg-gray-900 dark:text-gray-500 dark:border-gray-800'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800 dark:hover:bg-purple-900/20 dark:hover:text-purple-400 dark:hover:border-purple-800'}
              `}
            >
              <Star className={`h-4 w-4 ${savedFilterActive ? 'fill-white' : ''}`} />
              <span className="hidden xs:inline">Saved</span>
              {isBasicUser && <Lock className="h-3.5 w-3.5 ml-1 text-gray-500" />}
            </button>
            {showTooltip === 'saved' && isBasicUser && (
              <div className="absolute z-50 bg-gray-900 text-white text-sm px-3 py-2 rounded shadow-lg top-full mt-2 w-64 right-0">
                <div className="absolute -top-1 right-4 transform rotate-45 w-2 h-2 bg-gray-900"></div>
                Upgrade to Premium to view your saved templates.
              </div>
            )}
          </div>

          {/* Sort dropdown */}
          <div 
            ref={sortMenuRef} 
            className="flex items-center relative"
            onMouseEnter={handleSortMouseEnter}
            onMouseLeave={handleSortMouseLeave}
          >
            <button
              onClick={handleSortClick}
              disabled={isBasicUser}
              className={`flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium border 
                ${isBasicUser 
                  ? 'bg-white text-gray-500 border-gray-200 opacity-60 cursor-not-allowed dark:bg-gray-900 dark:text-gray-500 dark:border-gray-800'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800 dark:hover:bg-gray-800'}`}
            >
              {getSortIcon()}
              <span className="hidden xs:inline">{getSortText()}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${isSortMenuOpen ? 'rotate-180' : ''}`} />
              {isBasicUser && <Lock className="h-3.5 w-3.5 ml-1 text-gray-500" />}
            </button>
            {showSortTooltip && (
              <div className="absolute z-50 bg-gray-900 text-white text-sm px-3 py-2 rounded shadow-lg top-full mt-2 w-64 right-0">
                <div className="absolute -top-1 right-4 transform rotate-45 w-2 h-2 bg-gray-900"></div>
                {getSortDescription()}
              </div>
            )}
            {showTooltip === 'sort' && isBasicUser && (
              <div className="absolute z-50 bg-gray-900 text-white text-sm px-3 py-2 rounded shadow-lg top-full mt-2 w-64 right-0">
                <div className="absolute -top-1 right-4 transform rotate-45 w-2 h-2 bg-gray-900"></div>
                Upgrade to Premium to sort templates by newest or popularity.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Count and Clear Button - MODIFIED TO SHOW ONLY CLEAR BUTTON - Hidden for mail templates */}
      {!isMailTemplates && activeFilters.length > 0 && (
        <div className="flex items-center justify-start sm:justify-center mt-3 mb-3 text-sm text-gray-600 dark:text-gray-400 px-1">
          <button 
            onClick={clearAllFilters}
            disabled={isBasicUser}
            className={`flex items-center gap-1 ${
              isBasicUser 
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60' 
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            } transition-colors`}
          >
            <X className="h-4 w-4" />
            Clear Filters
          </button>
        </div>
      )}

      {/* More/Categories Dropdown Portal - Renders based on 'hiddenTags' state - Hidden for mail templates */}
      {!isMailTemplates && isMoreMenuOpen && (
        <div
          ref={dropdownRef}
          className="fixed z-50 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 max-h-[300px] overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            maxHeight: '300px'
          }}
        >
          <div className="py-2 px-2">
             {/* Only show tags that are hidden (not visible on the page) */}
             {hiddenTags
              .map((tag) => {
                const isActive = activeFilters.includes(tag);
                const isDisabled = !isActive && isFilterLimitReached && !isBasicUser;
                return (
                  <button
                    key={tag}
                    onClick={() => handleMoreTagSelect(tag)} 
                    disabled={isDisabled || isBasicUser}
                    className={`
                      w-full text-left px-3 py-2 rounded-md text-sm mb-1 flex justify-between items-center transition-colors
                      ${isActive 
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' 
                        : 'hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'}
                      ${(isDisabled || isBasicUser) ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                  >
                    <span>{tag}</span>
                    {isActive && (
                      <span className="text-purple-600 dark:text-purple-400 ml-2">✓</span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Sort Dropdown Portal */}
      {isSortMenuOpen && !isBasicUser && (
        <div
          ref={sortDropdownRef}
          className="fixed z-50 mt-2 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800"
          style={{
            top: `${sortDropdownPosition.top}px`,
            left: `${sortDropdownPosition.left}px`,
          }}
        >
          <div className="py-2">
            {['random', 'newest', 'popular'].map((method) => (
              <button
                key={method}
                onClick={() => handleSortSelect(method)}
                className={`
                  w-full text-left px-3 py-2 text-sm
                  ${sortMethod === method 
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' 
                    : 'hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'}
                `}
              >
                {method === 'random' && 'Randomized'}
                {method === 'newest' && 'Newly Added'}
                {method === 'popular' && 'Most Popular'}
                {sortMethod === method && (
                  <span className="float-right text-purple-600 dark:text-purple-400">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateFilters; 